import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Cpu,
  Loader2,
  Zap,
  HardDrive,
  BarChart2,
  Database,
  Percent,
  Flame,
  FileText,
  Clock,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import baseapi from '../../../services/baseapi';

interface OracleInstanceMemoryViewProps {
  activeCluster: string;
  timeRange: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

const formatNumber = (val: number | null | undefined, decimals = 2): string => {
  if (val == null || isNaN(val)) return '—';
  return Number(val).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatBytesRate = (val: number | null | undefined): string => {
  if (val == null || isNaN(val)) return '—';
  if (val >= 1024 * 1024) return `${(val / (1024 * 1024)).toFixed(2)} MB/s`;
  if (val >= 1024) return `${(val / 1024).toFixed(2)} KB/s`;
  return `${val.toFixed(1)} B/s`;
};

const ScrollableLegend = (props: any) => {
  const { payload, hoveredSeries, onHoverSeries } = props;
  if (!payload || payload.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 max-h-14 overflow-y-auto custom-scroll px-2 py-1 relative z-0">
      {payload.map((entry: any, index: number) => {
        const seriesKey = entry.dataKey || entry.value;
        const isHovered = hoveredSeries === seriesKey;
        const isDimmed = hoveredSeries !== null && !isHovered;
        return (
          <div
            key={`legend-item-${index}`}
            onMouseEnter={() => onHoverSeries && onHoverSeries(seriesKey)}
            onMouseLeave={() => onHoverSeries && onHoverSeries(null)}
            className={`flex items-center gap-1.5 shrink-0 cursor-pointer transition-all duration-200 ${isDimmed ? 'opacity-30 scale-95' : 'opacity-100 scale-95'
              }`}
          >
            <span
              className="w-2 h-2 rounded-full inline-block shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span
              className="text-gray-700 dark:text-gray-200 text-[10px] truncate max-w-[150px] font-medium"
              title={entry.value}
            >
              {entry.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const ChartTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border border-gray-200 dark:border-gray-700/80 rounded-lg p-2.5 shadow-xl text-[11px] text-gray-900 dark:text-gray-100 flex flex-col gap-1.5 min-w-[160px] z-50">
        {label !== undefined && label !== null && label !== '' && (
          <div className="text-gray-700 dark:text-gray-300 font-bold border-b border-gray-100 dark:border-gray-800 pb-1 mb-0.5 text-[11px] truncate flex items-center gap-1.5">
            <Clock className="w-3 h-3 text-orange-500 shrink-0" />
            <span>{label}</span>
          </div>
        )}
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-medium">
              <span
                className="inline-block w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: entry.color || entry.stroke || entry.fill || '#f97316' }}
              />
              {entry.name || entry.dataKey}
            </span>
            <span className="font-bold text-gray-900 dark:text-white font-mono">
              {typeof entry.value === 'number'
                ? entry.value.toLocaleString(undefined, { maximumFractionDigits: 2 })
                : (entry.value ?? '—')}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export const OracleInstanceMemoryView: React.FC<OracleInstanceMemoryViewProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [perfData, setPerfData] = useState<any>(null);

  // Legend hover states for each chart
  const [hoveredDbTime, setHoveredDbTime] = useState<string | null>(null);
  const [hoveredPga, setHoveredPga] = useState<string | null>(null);
  const [hoveredThroughput, setHoveredThroughput] = useState<string | null>(null);
  const [hoveredParses, setHoveredParses] = useState<string | null>(null);
  const [hoveredIo, setHoveredIo] = useState<string | null>(null);
  const [hoveredCache, setHoveredCache] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const minutes = parseInt(timeRange || '60', 10);
    const params: any = {
      cluster_id: activeCluster,
      minutes,
    };
    if (clientId) params.client_id = clientId;

    try {
      const res = await baseapi.get('/db_monitor_dashboard/graph/performance', { params });
      setPerfData(res.data?.data || res.data);
    } catch (err) {
      console.error('Error fetching Oracle performance telemetry:', err);
      setPerfData(null);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  // Formatted chart points
  const chartData = useMemo(() => {
    if (!perfData?.labels || !perfData?.series) return [];
    const labels = perfData.labels;
    const series = perfData.series;

    return labels.map((timestamp: string, idx: number) => {
      const d = new Date(timestamp);
      const isPastDay = parseInt(timeRange || '60', 10) > 1440;
      const formattedTime = !isNaN(d.getTime())
        ? isPastDay
          ? `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d
            .getDate()
            .toString()
            .padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d
              .getMinutes()
              .toString()
              .padStart(2, '0')}`
          : `${d.getHours().toString().padStart(2, '0')}:${d
            .getMinutes()
            .toString()
            .padStart(2, '0')}`
        : timestamp;

      return {
        time: formattedTime,
        rawTimestamp: timestamp,
        db_time: Number(series.oracle_db_time_per_sec?.[idx] ?? 0),
        cpu_time: Number(series.oracle_cpu_time_per_sec?.[idx] ?? 0),
        aas: Number(series.oracle_avg_active_sessions?.[idx] ?? 0),
        pga_allocated: Number(series.oracle_pga_allocated_mb?.[idx] ?? 0),
        pga_target: Number(series.oracle_pga_target_mb?.[idx] ?? 0),
        executions: Number(series.oracle_executions_per_sec?.[idx] ?? 0),
        qps: Number(series.qps?.[idx] ?? 0),
        commits: Number(series.oracle_user_commits_per_sec?.[idx] ?? 0),
        rollbacks: Number(series.oracle_user_rollbacks_per_sec?.[idx] ?? 0),
        parses_total: Number(series.oracle_parses_total_per_sec?.[idx] ?? 0),
        parses_hard: Number(series.oracle_parses_hard_per_sec?.[idx] ?? 0),
        logical_reads: Number(series.oracle_logical_reads_per_sec?.[idx] ?? 0),
        redo_bytes: Number(series.oracle_redo_bytes_per_sec?.[idx] ?? 0),
        cache_hit: Number(series.cache_hit_pct?.[idx] ?? 0),
      };
    });
  }, [perfData, timeRange]);

  const summary = perfData?.summary || {};
  const latest = summary.latest || {};
  const peak = summary.peak || {};
  const avg = summary.average || {};

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-white dark:bg-gray-800/40 rounded-xl border border-gray-200 dark:border-gray-700/60 min-h-[420px]">
        <Loader2 className="w-9 h-9 text-orange-500 animate-spin mb-3" />
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Loading Oracle Instance & Memory Telemetry...
        </span>
        <span className="text-xs text-gray-400 mt-1">
          Querying SGA/PGA metrics, session workload, and buffer cache telemetry
        </span>
      </div>
    );
  }

  if (!perfData || chartData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-white dark:bg-gray-800/40 rounded-xl border border-gray-200 dark:border-gray-700/60 min-h-[380px] text-center">
        <div className="w-12 h-12 rounded-xl bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/20 flex items-center justify-center text-orange-600 dark:text-orange-400 mb-3">
          <Database className="w-6 h-6" />
        </div>
        <h3 className="text-base font-bold text-gray-900 dark:text-white">
          No Oracle Performance Data Available
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-md">
          No instance and memory telemetry points were recorded for this cluster in the selected time range.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* 5 KPI Hero Cards (Clean Dark Mode) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {/* Card 1: DB Time vs CPU Time */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm transition-all hover:border-orange-300 dark:hover:border-orange-500/40 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                DB Time / Sec
              </span>
              <div className="w-6 h-6 rounded-lg bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 flex items-center justify-center">
                <Flame className="w-3.5 h-3.5" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-1 py-1.5 px-2 bg-gray-50 dark:bg-gray-900/60 rounded-lg border border-gray-100 dark:border-gray-800 mb-2">
              <div className="text-center border-r border-gray-200 dark:border-gray-800">
                <div className="text-[9px] uppercase font-bold text-gray-400">Latest</div>
                <div className="text-xs font-bold font-mono text-gray-900 dark:text-white">
                  {formatNumber(latest.oracle_db_time_per_sec)}s
                </div>
              </div>
              <div className="text-center border-r border-gray-200 dark:border-gray-800">
                <div className="text-[9px] uppercase font-bold text-gray-400">Peak</div>
                <div className="text-xs font-bold font-mono text-amber-600 dark:text-amber-400">
                  {formatNumber(peak.oracle_db_time_per_sec)}s
                </div>
              </div>
              <div className="text-center">
                <div className="text-[9px] uppercase font-bold text-gray-400">Average</div>
                <div className="text-xs font-bold font-mono text-gray-700 dark:text-gray-300">
                  {formatNumber(avg.oracle_db_time_per_sec)}s
                </div>
              </div>
            </div>
          </div>

          <div className="pt-2 border-t border-gray-100 dark:border-gray-800/80 flex items-center justify-between text-[11px]">
            <span className="text-gray-500 dark:text-gray-400">CPU Time / s:</span>
            <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
              {formatNumber(latest.oracle_cpu_time_per_sec)}s (AAS: {formatNumber(latest.oracle_avg_active_sessions)})
            </span>
          </div>
        </div>

        {/* Card 2: PGA Memory Allocation */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm transition-all hover:border-emerald-300 dark:hover:border-emerald-500/40 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                PGA Allocated
              </span>
              <div className="w-6 h-6 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                <Cpu className="w-3.5 h-3.5" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-1 py-1.5 px-2 bg-gray-50 dark:bg-gray-900/60 rounded-lg border border-gray-100 dark:border-gray-800 mb-2">
              <div className="text-center border-r border-gray-200 dark:border-gray-800">
                <div className="text-[9px] uppercase font-bold text-gray-400">Latest</div>
                <div className="text-xs font-bold font-mono text-gray-900 dark:text-white">
                  {formatNumber(latest.oracle_pga_allocated_mb)} MB
                </div>
              </div>
              <div className="text-center border-r border-gray-200 dark:border-gray-800">
                <div className="text-[9px] uppercase font-bold text-gray-400">Peak</div>
                <div className="text-xs font-bold font-mono text-emerald-600 dark:text-emerald-400">
                  {formatNumber(peak.oracle_pga_allocated_mb)} MB
                </div>
              </div>
              <div className="text-center">
                <div className="text-[9px] uppercase font-bold text-gray-400">Average</div>
                <div className="text-xs font-bold font-mono text-gray-700 dark:text-gray-300">
                  {formatNumber(avg.oracle_pga_allocated_mb)} MB
                </div>
              </div>
            </div>
          </div>

          <div className="pt-2 border-t border-gray-100 dark:border-gray-800/80 flex items-center justify-between text-[11px]">
            <span className="text-gray-500 dark:text-gray-400">Target Config:</span>
            <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
              {Number(latest.oracle_pga_target_mb || 0) > 0
                ? `${formatNumber(latest.oracle_pga_target_mb)} MB`
                : 'Auto Managed'}
            </span>
          </div>
        </div>

        {/* Card 3: SQL Executions & QPS */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm transition-all hover:border-blue-300 dark:hover:border-blue-500/40 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                SQL Executions / Sec
              </span>
              <div className="w-6 h-6 rounded-lg bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                <Zap className="w-3.5 h-3.5" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-1 py-1.5 px-2 bg-gray-50 dark:bg-gray-900/60 rounded-lg border border-gray-100 dark:border-gray-800 mb-2">
              <div className="text-center border-r border-gray-200 dark:border-gray-800">
                <div className="text-[9px] uppercase font-bold text-gray-400">Latest</div>
                <div className="text-xs font-bold font-mono text-gray-900 dark:text-white">
                  {formatNumber(latest.oracle_executions_per_sec)}/s
                </div>
              </div>
              <div className="text-center border-r border-gray-200 dark:border-gray-800">
                <div className="text-[9px] uppercase font-bold text-gray-400">Peak</div>
                <div className="text-xs font-bold font-mono text-blue-600 dark:text-blue-400">
                  {formatNumber(peak.oracle_executions_per_sec)}/s
                </div>
              </div>
              <div className="text-center">
                <div className="text-[9px] uppercase font-bold text-gray-400">Average</div>
                <div className="text-xs font-bold font-mono text-gray-700 dark:text-gray-300">
                  {formatNumber(avg.oracle_executions_per_sec)}/s
                </div>
              </div>
            </div>
          </div>

          <div className="pt-2 border-t border-gray-100 dark:border-gray-800/80 flex items-center justify-between text-[11px]">
            <span className="text-gray-500 dark:text-gray-400">Current QPS:</span>
            <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
              {formatNumber(latest.qps, 1)} queries/sec
            </span>
          </div>
        </div>

        {/* Card 4: Total & Hard Parses */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm transition-all hover:border-purple-300 dark:hover:border-purple-500/40 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Parses / Sec
              </span>
              <div className="w-6 h-6 rounded-lg bg-purple-50 dark:bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center">
                <FileText className="w-3.5 h-3.5" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-1 py-1.5 px-2 bg-gray-50 dark:bg-gray-900/60 rounded-lg border border-gray-100 dark:border-gray-800 mb-2">
              <div className="text-center border-r border-gray-200 dark:border-gray-800">
                <div className="text-[9px] uppercase font-bold text-gray-400">Latest</div>
                <div className="text-xs font-bold font-mono text-gray-900 dark:text-white">
                  {formatNumber(latest.oracle_parses_total_per_sec)}/s
                </div>
              </div>
              <div className="text-center border-r border-gray-200 dark:border-gray-800">
                <div className="text-[9px] uppercase font-bold text-gray-400">Peak</div>
                <div className="text-xs font-bold font-mono text-purple-600 dark:text-purple-400">
                  {formatNumber(peak.oracle_parses_total_per_sec)}/s
                </div>
              </div>
              <div className="text-center">
                <div className="text-[9px] uppercase font-bold text-gray-400">Average</div>
                <div className="text-xs font-bold font-mono text-gray-700 dark:text-gray-300">
                  {formatNumber(avg.oracle_parses_total_per_sec)}/s
                </div>
              </div>
            </div>
          </div>

          <div className="pt-2 border-t border-gray-100 dark:border-gray-800/80 flex items-center justify-between text-[11px]">
            <span className="text-gray-500 dark:text-gray-400">Hard Parses:</span>
            <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
              {formatNumber(latest.oracle_parses_hard_per_sec, 2)}/s (Soft: 100%)
            </span>
          </div>
        </div>

        {/* Card 5: Buffer Cache Hit & Redo */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm transition-all hover:border-teal-300 dark:hover:border-teal-500/40 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Buffer Cache Hit
              </span>
              <div className="w-6 h-6 rounded-lg bg-teal-50 dark:bg-teal-500/10 text-teal-600 dark:text-teal-400 flex items-center justify-center">
                <Percent className="w-3.5 h-3.5" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-1 py-1.5 px-2 bg-gray-50 dark:bg-gray-900/60 rounded-lg border border-gray-100 dark:border-gray-800 mb-2">
              <div className="text-center border-r border-gray-200 dark:border-gray-800">
                <div className="text-[9px] uppercase font-bold text-gray-400">Latest</div>
                <div className="text-xs font-bold font-mono text-gray-900 dark:text-white">
                  {formatNumber(latest.cache_hit_pct, 1)}%
                </div>
              </div>
              <div className="text-center border-r border-gray-200 dark:border-gray-800">
                <div className="text-[9px] uppercase font-bold text-gray-400">Peak</div>
                <div className="text-xs font-bold font-mono text-teal-600 dark:text-teal-400">
                  {formatNumber(peak.cache_hit_pct, 1)}%
                </div>
              </div>
              <div className="text-center">
                <div className="text-[9px] uppercase font-bold text-gray-400">Average</div>
                <div className="text-xs font-bold font-mono text-gray-700 dark:text-gray-300">
                  {formatNumber(avg.cache_hit_pct, 1)}%
                </div>
              </div>
            </div>
          </div>

          <div className="pt-2 border-t border-gray-100 dark:border-gray-800/80 flex items-center justify-between text-[11px]">
            <span className="text-gray-500 dark:text-gray-400">Logical Reads:</span>
            <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
              {formatNumber(latest.oracle_logical_reads_per_sec, 0)}/s
            </span>
          </div>
        </div>
      </div>

      {/* 6 Recharts Telemetry Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Chart 1: DB Time vs CPU Time (Workload) */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-xs font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                <Flame className="w-4 h-4 text-orange-500" />
                Database Time vs. CPU Time
              </h3>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                Workload demand per second; DB Time above CPU Time denotes active waits
              </p>
            </div>
            <div className="text-right">
              <span className="text-xs font-mono font-bold text-orange-600 dark:text-orange-400">
                {formatNumber(latest.oracle_db_time_per_sec)}s
              </span>
              <span className="text-[10px] text-gray-400 block">Latest DB Time</span>
            </div>
          </div>

          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 12, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="dbTimeGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0.0} />
                  </linearGradient>
                  <linearGradient id="cpuTimeGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.2} vertical={false} />
                <XAxis
                  dataKey="time"
                  stroke="#6b7280"
                  fontSize={10}
                  tickLine={false}
                  axisLine={{ stroke: '#4b5563', strokeOpacity: 0.3 }}
                />
                <YAxis
                  stroke="#6b7280"
                  fontSize={10}
                  tickLine={false}
                  axisLine={{ stroke: '#4b5563', strokeOpacity: 0.3 }}
                  tickFormatter={(val) => `${val}s`}
                />
                <Tooltip content={<ChartTooltip />} />
                <Legend
                  content={<ScrollableLegend hoveredSeries={hoveredDbTime} onHoverSeries={setHoveredDbTime} />}
                />
                <Area
                  type="monotone"
                  dataKey="db_time"
                  name="DB Time / Sec"
                  stroke="#f97316"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#dbTimeGrad)"
                  opacity={hoveredDbTime && hoveredDbTime !== 'DB Time / Sec' ? 0.2 : 1}
                />
                <Area
                  type="monotone"
                  dataKey="cpu_time"
                  name="CPU Time / Sec"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#cpuTimeGrad)"
                  opacity={hoveredDbTime && hoveredDbTime !== 'CPU Time / Sec' ? 0.2 : 1}
                />
                <Line
                  type="monotone"
                  dataKey="aas"
                  name="Avg Active Sessions"
                  stroke="#8b5cf6"
                  strokeWidth={1.8}
                  strokeDasharray="4 4"
                  dot={false}
                  opacity={hoveredDbTime && hoveredDbTime !== 'Avg Active Sessions' ? 0.2 : 1}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Chart 2: PGA Memory Usage */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-xs font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                <Cpu className="w-4 h-4 text-emerald-500" />
                PGA Memory Usage (Allocated vs Target)
              </h3>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                Private Global Area memory assigned to Oracle background & server processes
              </p>
            </div>
            <div className="text-right">
              <span className="text-xs font-mono font-bold text-emerald-600 dark:text-emerald-400">
                {formatNumber(latest.oracle_pga_allocated_mb)} MB
              </span>
              <span className="text-[10px] text-gray-400 block">Allocated PGA</span>
            </div>
          </div>

          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 12, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="pgaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.2} vertical={false} />
                <XAxis
                  dataKey="time"
                  stroke="#6b7280"
                  fontSize={10}
                  tickLine={false}
                  axisLine={{ stroke: '#4b5563', strokeOpacity: 0.3 }}
                />
                <YAxis
                  stroke="#6b7280"
                  fontSize={10}
                  tickLine={false}
                  axisLine={{ stroke: '#4b5563', strokeOpacity: 0.3 }}
                  tickFormatter={(val) => `${val}M`}
                />
                <Tooltip content={<ChartTooltip />} />
                <Legend content={<ScrollableLegend hoveredSeries={hoveredPga} onHoverSeries={setHoveredPga} />} />
                <Area
                  type="monotone"
                  dataKey="pga_allocated"
                  name="PGA Allocated (MB)"
                  stroke="#10b981"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#pgaGrad)"
                  opacity={hoveredPga && hoveredPga !== 'PGA Allocated (MB)' ? 0.2 : 1}
                />
                {Number(latest.oracle_pga_target_mb || 0) > 0 && (
                  <Line
                    type="step"
                    dataKey="pga_target"
                    name="PGA Target (MB)"
                    stroke="#ef4444"
                    strokeWidth={1.5}
                    strokeDasharray="5 5"
                    dot={false}
                    opacity={hoveredPga && hoveredPga !== 'PGA Target (MB)' ? 0.2 : 1}
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Chart 3: Query Throughput & Execution Rate */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-xs font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                <Zap className="w-4 h-4 text-blue-500" />
                Query Throughput & Execution Rates
              </h3>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                SQL Statements Executed and Queries Per Second (QPS)
              </p>
            </div>
            <div className="text-right">
              <span className="text-xs font-mono font-bold text-blue-600 dark:text-blue-400">
                {formatNumber(latest.oracle_executions_per_sec)} /s
              </span>
              <span className="text-[10px] text-gray-400 block">Executions</span>
            </div>
          </div>

          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 12, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="execGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.2} vertical={false} />
                <XAxis
                  dataKey="time"
                  stroke="#6b7280"
                  fontSize={10}
                  tickLine={false}
                  axisLine={{ stroke: '#4b5563', strokeOpacity: 0.3 }}
                />
                <YAxis
                  stroke="#6b7280"
                  fontSize={10}
                  tickLine={false}
                  axisLine={{ stroke: '#4b5563', strokeOpacity: 0.3 }}
                  tickFormatter={(val) => `${val}/s`}
                />
                <Tooltip content={<ChartTooltip />} />
                <Legend
                  content={
                    <ScrollableLegend
                      hoveredSeries={hoveredThroughput}
                      onHoverSeries={setHoveredThroughput}
                    />
                  }
                />
                <Area
                  type="monotone"
                  dataKey="executions"
                  name="Executions / Sec"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#execGrad)"
                  opacity={hoveredThroughput && hoveredThroughput !== 'Executions / Sec' ? 0.2 : 1}
                />
                <Line
                  type="monotone"
                  dataKey="qps"
                  name="QPS (Queries / Sec)"
                  stroke="#f59e0b"
                  strokeWidth={1.8}
                  dot={false}
                  opacity={hoveredThroughput && hoveredThroughput !== 'QPS (Queries / Sec)' ? 0.2 : 1}
                />
                <Line
                  type="monotone"
                  dataKey="commits"
                  name="Commits / Sec"
                  stroke="#10b981"
                  strokeWidth={1.5}
                  strokeDasharray="3 3"
                  dot={false}
                  opacity={hoveredThroughput && hoveredThroughput !== 'Commits / Sec' ? 0.2 : 1}
                />
                <Line
                  type="monotone"
                  dataKey="rollbacks"
                  name="Rollbacks / Sec"
                  stroke="#ef4444"
                  strokeWidth={1.5}
                  strokeDasharray="3 3"
                  dot={false}
                  opacity={hoveredThroughput && hoveredThroughput !== 'Rollbacks / Sec' ? 0.2 : 1}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Chart 4: SQL Parsing Activity */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-xs font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                <FileText className="w-4 h-4 text-purple-500" />
                SQL Parsing Activity (Soft vs Hard Parses)
              </h3>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                Hard parses indicate missing bind variables or shared pool sizing issues
              </p>
            </div>
            <div className="text-right">
              <span className="text-xs font-mono font-bold text-purple-600 dark:text-purple-400">
                {formatNumber(latest.oracle_parses_total_per_sec)} /s
              </span>
              <span className="text-[10px] text-gray-400 block">Total Parses</span>
            </div>
          </div>

          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 12, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="parseGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.2} vertical={false} />
                <XAxis
                  dataKey="time"
                  stroke="#6b7280"
                  fontSize={10}
                  tickLine={false}
                  axisLine={{ stroke: '#4b5563', strokeOpacity: 0.3 }}
                />
                <YAxis
                  stroke="#6b7280"
                  fontSize={10}
                  tickLine={false}
                  axisLine={{ stroke: '#4b5563', strokeOpacity: 0.3 }}
                  tickFormatter={(val) => `${val}/s`}
                />
                <Tooltip content={<ChartTooltip />} />
                <Legend
                  content={
                    <ScrollableLegend hoveredSeries={hoveredParses} onHoverSeries={setHoveredParses} />}
                />
                <Area
                  type="monotone"
                  dataKey="parses_total"
                  name="Total Parses / Sec"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#parseGrad)"
                  opacity={hoveredParses && hoveredParses !== 'Total Parses / Sec' ? 0.2 : 1}
                />
                <Line
                  type="monotone"
                  dataKey="parses_hard"
                  name="Hard Parses / Sec"
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={false}
                  opacity={hoveredParses && hoveredParses !== 'Hard Parses / Sec' ? 0.2 : 1}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Chart 5: Logical Reads & Redo Generation */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-xs font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                <HardDrive className="w-4 h-4 text-indigo-500" />
                Logical Reads & Redo Rate
              </h3>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                Memory buffer requests (Logical Reads) and Write Ahead Log generation (Redo)
              </p>
            </div>
            <div className="text-right">
              <span className="text-xs font-mono font-bold text-indigo-600 dark:text-indigo-400">
                {formatNumber(latest.oracle_logical_reads_per_sec, 0)} /s
              </span>
              <span className="text-[10px] text-gray-400 block">Logical Reads</span>
            </div>
          </div>

          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 12, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.2} vertical={false} />
                <XAxis
                  dataKey="time"
                  stroke="#6b7280"
                  fontSize={10}
                  tickLine={false}
                  axisLine={{ stroke: '#4b5563', strokeOpacity: 0.3 }}
                />
                <YAxis
                  yAxisId="left"
                  stroke="#6366f1"
                  fontSize={10}
                  tickLine={false}
                  axisLine={{ stroke: '#6366f1', strokeOpacity: 0.3 }}
                  tickFormatter={(val) => `${val}/s`}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="#f97316"
                  fontSize={10}
                  tickLine={false}
                  axisLine={{ stroke: '#f97316', strokeOpacity: 0.3 }}
                  tickFormatter={(val) => (val >= 1024 ? `${(val / 1024).toFixed(0)}K` : `${val}B`)}
                />
                <Tooltip content={<ChartTooltip />} />
                <Legend content={<ScrollableLegend hoveredSeries={hoveredIo} onHoverSeries={setHoveredIo} />} />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="logical_reads"
                  name="Logical Reads / Sec"
                  stroke="#6366f1"
                  strokeWidth={2}
                  dot={false}
                  opacity={hoveredIo && hoveredIo !== 'Logical Reads / Sec' ? 0.2 : 1}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="redo_bytes"
                  name="Redo Bytes / Sec"
                  stroke="#f97316"
                  strokeWidth={2}
                  dot={false}
                  opacity={hoveredIo && hoveredIo !== 'Redo Bytes / Sec' ? 0.2 : 1}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Chart 6: Buffer Cache Hit Ratio (%) */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-xs font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                <Percent className="w-4 h-4 text-teal-500" />
                Buffer Cache Hit Ratio
              </h3>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                Percentage of block reads satisfied immediately from SGA Database Buffer Cache
              </p>
            </div>
            <div className="text-right">
              <span className="text-xs font-mono font-bold text-teal-600 dark:text-teal-400">
                {formatNumber(latest.cache_hit_pct, 1)}%
              </span>
              <span className="text-[10px] text-gray-400 block">Cache Hit</span>
            </div>
          </div>

          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 12, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="cacheGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#14b8a6" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.2} vertical={false} />
                <XAxis
                  dataKey="time"
                  stroke="#6b7280"
                  fontSize={10}
                  tickLine={false}
                  axisLine={{ stroke: '#4b5563', strokeOpacity: 0.3 }}
                />
                <YAxis
                  domain={[0, 100]}
                  stroke="#6b7280"
                  fontSize={10}
                  tickLine={false}
                  axisLine={{ stroke: '#4b5563', strokeOpacity: 0.3 }}
                  tickFormatter={(val) => `${val}%`}
                />
                <Tooltip content={<ChartTooltip />} />
                <Legend content={<ScrollableLegend hoveredSeries={hoveredCache} onHoverSeries={setHoveredCache} />} />
                <Area
                  type="monotone"
                  dataKey="cache_hit"
                  name="Cache Hit Ratio (%)"
                  stroke="#14b8a6"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#cacheGrad)"
                  opacity={hoveredCache && hoveredCache !== 'Cache Hit Ratio (%)' ? 0.2 : 1}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Summary Metrics Table */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl overflow-hidden shadow-sm">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700/60 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart2 className="w-4 h-4 text-orange-500" />
            <h3 className="text-xs font-bold text-gray-900 dark:text-white">
              Instance & Memory Telemetry Breakdown
            </h3>
          </div>
          <span className="text-[11px] text-gray-500 dark:text-gray-400">
            Window: {chartData.length} samples collected
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-gray-50/80 dark:bg-gray-900/60 text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800 uppercase text-[10px] tracking-wider font-semibold">
              <tr>
                <th className="px-4 py-2.5">Metric</th>
                <th className="px-4 py-2.5 text-right">Latest</th>
                <th className="px-4 py-2.5 text-right">Peak (Max)</th>
                <th className="px-4 py-2.5 text-right">Average</th>
                <th className="px-4 py-2.5">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800/60 text-gray-700 dark:text-gray-200 font-mono text-[11px]">
              <tr className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors">
                <td className="px-4 py-2.5 font-sans font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />
                  Database Time
                </td>
                <td className="px-4 py-2.5 text-right font-bold text-orange-600 dark:text-orange-400">
                  {formatNumber(latest.oracle_db_time_per_sec)} s/s
                </td>
                <td className="px-4 py-2.5 text-right">{formatNumber(peak.oracle_db_time_per_sec)} s/s</td>
                <td className="px-4 py-2.5 text-right">{formatNumber(avg.oracle_db_time_per_sec)} s/s</td>
                <td className="px-4 py-2.5 font-sans text-gray-500 dark:text-gray-400 text-[10px]">
                  Total time spent by Oracle processes in database calls
                </td>
              </tr>

              <tr className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors">
                <td className="px-4 py-2.5 font-sans font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                  CPU Time
                </td>
                <td className="px-4 py-2.5 text-right font-bold text-blue-600 dark:text-blue-400">
                  {formatNumber(latest.oracle_cpu_time_per_sec)} s/s
                </td>
                <td className="px-4 py-2.5 text-right">{formatNumber(peak.oracle_cpu_time_per_sec)} s/s</td>
                <td className="px-4 py-2.5 text-right">{formatNumber(avg.oracle_cpu_time_per_sec)} s/s</td>
                <td className="px-4 py-2.5 font-sans text-gray-500 dark:text-gray-400 text-[10px]">
                  CPU execution seconds consumed by Oracle user and system sessions
                </td>
              </tr>

              <tr className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors">
                <td className="px-4 py-2.5 font-sans font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-purple-500 shrink-0" />
                  Average Active Sessions (AAS)
                </td>
                <td className="px-4 py-2.5 text-right font-bold text-purple-600 dark:text-purple-400">
                  {formatNumber(latest.oracle_avg_active_sessions)}
                </td>
                <td className="px-4 py-2.5 text-right">{formatNumber(peak.oracle_avg_active_sessions)}</td>
                <td className="px-4 py-2.5 text-right">{formatNumber(avg.oracle_avg_active_sessions)}</td>
                <td className="px-4 py-2.5 font-sans text-gray-500 dark:text-gray-400 text-[10px]">
                  Average number of active sessions concurrently running or waiting
                </td>
              </tr>

              <tr className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors">
                <td className="px-4 py-2.5 font-sans font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                  PGA Allocated Memory
                </td>
                <td className="px-4 py-2.5 text-right font-bold text-emerald-600 dark:text-emerald-400">
                  {formatNumber(latest.oracle_pga_allocated_mb)} MB
                </td>
                <td className="px-4 py-2.5 text-right">{formatNumber(peak.oracle_pga_allocated_mb)} MB</td>
                <td className="px-4 py-2.5 text-right">{formatNumber(avg.oracle_pga_allocated_mb)} MB</td>
                <td className="px-4 py-2.5 font-sans text-gray-500 dark:text-gray-400 text-[10px]">
                  Current total Program Global Area memory allocated by instance
                </td>
              </tr>

              <tr className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors">
                <td className="px-4 py-2.5 font-sans font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                  SQL Executions
                </td>
                <td className="px-4 py-2.5 text-right font-bold text-amber-600 dark:text-amber-400">
                  {formatNumber(latest.oracle_executions_per_sec)}/s
                </td>
                <td className="px-4 py-2.5 text-right">{formatNumber(peak.oracle_executions_per_sec)}/s</td>
                <td className="px-4 py-2.5 text-right">{formatNumber(avg.oracle_executions_per_sec)}/s</td>
                <td className="px-4 py-2.5 font-sans text-gray-500 dark:text-gray-400 text-[10px]">
                  Rate of SQL statements executed per second
                </td>
              </tr>

              <tr className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors">
                <td className="px-4 py-2.5 font-sans font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-cyan-500 shrink-0" />
                  QPS (Queries Per Sec)
                </td>
                <td className="px-4 py-2.5 text-right font-bold text-cyan-600 dark:text-cyan-400">
                  {formatNumber(latest.qps, 1)}/s
                </td>
                <td className="px-4 py-2.5 text-right">{formatNumber(peak.qps, 1)}/s</td>
                <td className="px-4 py-2.5 text-right">{formatNumber(avg.qps, 1)}/s</td>
                <td className="px-4 py-2.5 font-sans text-gray-500 dark:text-gray-400 text-[10px]">
                  Total query transactions per second
                </td>
              </tr>

              <tr className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors">
                <td className="px-4 py-2.5 font-sans font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-indigo-500 shrink-0" />
                  Total Parses
                </td>
                <td className="px-4 py-2.5 text-right font-bold text-indigo-600 dark:text-indigo-400">
                  {formatNumber(latest.oracle_parses_total_per_sec)}/s
                </td>
                <td className="px-4 py-2.5 text-right">{formatNumber(peak.oracle_parses_total_per_sec)}/s</td>
                <td className="px-4 py-2.5 text-right">{formatNumber(avg.oracle_parses_total_per_sec)}/s</td>
                <td className="px-4 py-2.5 font-sans text-gray-500 dark:text-gray-400 text-[10px]">
                  Total parses (soft + hard) per second
                </td>
              </tr>

              <tr className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors">
                <td className="px-4 py-2.5 font-sans font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0" />
                  Hard Parses
                </td>
                <td className="px-4 py-2.5 text-right font-bold text-rose-600 dark:text-rose-400">
                  {formatNumber(latest.oracle_parses_hard_per_sec, 2)}/s
                </td>
                <td className="px-4 py-2.5 text-right">{formatNumber(peak.oracle_parses_hard_per_sec, 2)}/s</td>
                <td className="px-4 py-2.5 text-right">{formatNumber(avg.oracle_parses_hard_per_sec, 2)}/s</td>
                <td className="px-4 py-2.5 font-sans text-gray-500 dark:text-gray-400 text-[10px]">
                  New SQL optimization calls needing shared pool compile
                </td>
              </tr>

              <tr className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors">
                <td className="px-4 py-2.5 font-sans font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-violet-500 shrink-0" />
                  Logical Reads
                </td>
                <td className="px-4 py-2.5 text-right font-bold text-violet-600 dark:text-violet-400">
                  {formatNumber(latest.oracle_logical_reads_per_sec, 1)}/s
                </td>
                <td className="px-4 py-2.5 text-right">{formatNumber(peak.oracle_logical_reads_per_sec, 1)}/s</td>
                <td className="px-4 py-2.5 text-right">{formatNumber(avg.oracle_logical_reads_per_sec, 1)}/s</td>
                <td className="px-4 py-2.5 font-sans text-gray-500 dark:text-gray-400 text-[10px]">
                  Database blocks read from buffer cache (consistent + current gets)
                </td>
              </tr>

              <tr className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors">
                <td className="px-4 py-2.5 font-sans font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
                  Redo Generated
                </td>
                <td className="px-4 py-2.5 text-right font-bold text-red-600 dark:text-red-400">
                  {formatBytesRate(latest.oracle_redo_bytes_per_sec)}
                </td>
                <td className="px-4 py-2.5 text-right">{formatBytesRate(peak.oracle_redo_bytes_per_sec)}</td>
                <td className="px-4 py-2.5 text-right">{formatBytesRate(avg.oracle_redo_bytes_per_sec)}</td>
                <td className="px-4 py-2.5 font-sans text-gray-500 dark:text-gray-400 text-[10px]">
                  Rate of redo log generation for ACID crash recovery
                </td>
              </tr>

              <tr className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors">
                <td className="px-4 py-2.5 font-sans font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-teal-500 shrink-0" />
                  Buffer Cache Hit Ratio
                </td>
                <td className="px-4 py-2.5 text-right font-bold text-teal-600 dark:text-teal-400">
                  {formatNumber(latest.cache_hit_pct, 1)}%
                </td>
                <td className="px-4 py-2.5 text-right">{formatNumber(peak.cache_hit_pct, 1)}%</td>
                <td className="px-4 py-2.5 text-right">{formatNumber(avg.cache_hit_pct, 1)}%</td>
                <td className="px-4 py-2.5 font-sans text-gray-500 dark:text-gray-400 text-[10px]">
                  SGA Database Buffer Cache hit efficiency
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OracleInstanceMemoryView;
